import React, { useState } from "react";
import CustomTimePicker from "../../../../component/elements/customTimePicker";
import CustomCheckbox from "../../../../component/elements/checkboxCustom";
import dayjs from "dayjs";
import NoteAddIcon from "@mui/icons-material/NoteAdd";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import CustomPopupForm from "../../../../component/customPopupModel";
import { useSnackbar } from "../../../../component/SnackbarContext";

const ApplyToAllTimePicker = ({
  applyToAll,
  handleApplyToAllChange,
  globalTime,
  handleGlobalTimeChange,
  error,
}) => (
  <div className="border-b border-[#e0e0e0] pb-4">
    <div className="grid grid-cols-1 md:grid-cols-[100px_minmax(120px,160px)_auto_minmax(120px,160px)_1fr] items-center gap-4">
      <CustomCheckbox
        label="Apply to all"
        checked={applyToAll}
        onChange={handleApplyToAllChange}
      />
      <CustomTimePicker
        label="Open"
        value={globalTime.open}
        onChange={(val) => handleGlobalTimeChange("open", val)}
      />
      <div className="text-[14px] text-center">to</div>
      <CustomTimePicker
        label="Close"
        value={globalTime.close}
        onChange={(val) => handleGlobalTimeChange("close", val)}
      />
      <div />
    </div>
    {error && (
      <div className="text-xs text-red-500 md:col-start-2 md:col-span-3 ml-0 md:ml-[120px] mt-1">
        {error}
      </div>
    )}
  </div>
);

const DayTimePickerRow = ({
  day,
  start,
  end,
  note,
  error,
  handleTimeChange,
  handleNoteClick,
}) => (
  <div className="border-b border-[#e0e0e0] md:border-b-0 pt-4 last:border-b-0">
    <div className="grid grid-cols-1 md:grid-cols-[100px_minmax(120px,160px)_auto_minmax(120px,160px)_1fr] items-start gap-4">
      <div className="text-[14px] font-[500] text-[#222222]">{day}</div>
      <CustomTimePicker
        label="Open"
        value={start}
        onChange={(val) => handleTimeChange(day, "open", val)}
      />
      <div className="text-[14px] text-center">to</div>
      <CustomTimePicker
        label="Close"
        value={end}
        onChange={(val) => handleTimeChange(day, "close", val)}
      />
      <div
        className={`flex items-center cursor-pointer text-[14px] font-[500] mb-2 md:mb-0 ${
          note ? "text-green-600" : "text-[#3587B8]"
        }`}
        onClick={() => handleNoteClick(day)}
      >
        <NoteAddIcon
          fontSize="small"
          className={`mr-1 ${note ? "text-green-600" : "text-[#3587B8]"}`}
        />
        {note ? "Edit Note" : "Add Note"}
      </div>
    </div>
    {error && (
      <div className="text-xs text-red-500 md:col-start-2 md:col-span-3 ml-0 md:ml-[120px] mt-1">
        {error}
      </div>
    )}
  </div>
);

const StoreHours = ({
  applyToAll,
  handleApplyToAllChange,
  globalTime,
  handleGlobalTimeChange,
  daysOfWeek,
  storeHours,
  handleTimeChange,
  setErrorsInParent 
}) => {
  const [open, setOpen] = useState(false);
  const [selectedDay, setSelectedDay] = useState("");
  const [noteValue, setNoteValue] = useState("");
  const [errors, setErrors] = useState({});
  const showSnackbar = useSnackbar();

  const handleNoteClick = (day) => {
    setSelectedDay(day);
    setNoteValue(storeHours[day]?.note || "");
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedDay("");
    setNoteValue("");
  };

  const handleSubmit = (note) => {
    handleTimeChange(selectedDay, "note", note);
    handleClose();
  };

  // Validate open < close
  const validateTime = (key, openVal, closeVal) => {
    if (openVal && closeVal && dayjs(openVal).isAfter(dayjs(closeVal))) {
      setErrors((prev) => {
        const updated = {
          ...prev,
          [key]: "Open time must be before close time",
        };
        setErrorsInParent(updated);
        return updated;
      });
    } else {
      setErrors((prev) => {
        const updated = { ...prev };
        delete updated[key];
        setErrorsInParent(updated);
        return updated;
      });
    }
  };

  // Wrap handleTimeChange for per-day validation
  const wrappedHandleTimeChange = (day, field, val) => {
    handleTimeChange(day, field, val);
    const updated = { ...storeHours[day], [field]: val };
    validateTime(day, updated.open, updated.close);
  };

  const wrappedHandleGlobalTimeChange = (field, val) => {
    const updated = { ...globalTime, [field]: val };
    if (
      updated.open &&
      updated.close &&
      dayjs(updated.open).isAfter(dayjs(updated.close))
    ) {
      setErrors((prev) => {
        const updated = {
          ...prev,
          global: "Open time must be before close time",
        };
        setErrorsInParent(updated);
        return updated;
      });
      showSnackbar("Open time must be before close time", "error");
    } else {
     setErrors(() => {
        setErrorsInParent({});
        return {};
      });
      handleGlobalTimeChange(field, val);
    }
  };



  return (
    <>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <ApplyToAllTimePicker
          applyToAll={applyToAll}
          handleApplyToAllChange={handleApplyToAllChange}
          globalTime={globalTime}
          handleGlobalTimeChange={wrappedHandleGlobalTimeChange}
          error={errors.global}
        />

        {daysOfWeek.map((day) => {
          const { open, close, note } = storeHours[day];
          return (
            <DayTimePickerRow
              key={day}
              day={day}
              start={open}
              end={close}
              note={note}
              error={errors[day]}
              handleTimeChange={wrappedHandleTimeChange}
              handleNoteClick={handleNoteClick}
            />
          );
        })}
      </LocalizationProvider>

      <CustomPopupForm
        isOpen={open}
        handleClose={handleClose}
        dialogType={"add-note"}
        onSubmit={handleSubmit}
        dialogDetails={{
          defaultValue: noteValue,
        }}
      />
    </>
  );
};

export default StoreHours;
